import api from "@/helpers/api";
import { DEFAULT_TZ } from "@/helpers/dateHelpers";

const STORAGE_KEY = "currentCompany";

// Subset of the company DTO that's safe to keep in localStorage. Full DTO
// carries GSTIN/PAN/TAN/Address — regulated identifiers we should not bake
// into client storage by default. In-memory Vuex state holds the full DTO
// so print headers / forms can read GSTIN/address without an extra fetch;
// only this trimmed projection is persisted to localStorage.
function projectForStorage(company) {
  if (!company) return null;
  return {
    id: company.id,
    name: company.name,
    shortName: company.shortName,
    timeZoneId: company.timeZoneId,
    currencySymbol: company.currencySymbol,
  };
}

function loadCachedCompany() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export const state = {
  // Seed synchronously from localStorage so the first render after refresh
  // already has the company TZ — avoids a flash of wrong-TZ formatting.
  currentCompany: loadCachedCompany(),
  loading: false,
};

export const getters = {
  currentCompany: (s) => s.currentCompany,
  timeZoneId: (s) => s.currentCompany?.timeZoneId || DEFAULT_TZ,
};

export const mutations = {
  SET_CURRENT_COMPANY(state, company) {
    state.currentCompany = company || null;
    const trimmed = projectForStorage(company);
    if (trimmed) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  },
  SET_LOADING(state, flag) {
    state.loading = flag;
  },
};

export const actions = {
  async hydrate({ commit, state: s }) {
    if (s.loading) return;
    commit("SET_LOADING", true);
    try {
      const { data } = await api.get("/companies/current");
      commit("SET_CURRENT_COMPANY", data);
    } catch (err) {
      // Don't blow up — fallback TZ keeps the app working.
      console.warn("company/hydrate failed:", err?.message || err);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  reset({ commit }) {
    commit("SET_CURRENT_COMPANY", null);
  },
};
